import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { itineraryApi } from "@/lib/api";
import type {
  ItemSegment,
  ItineraryItemType,
  ItineraryItemInput,
  ItineraryCompanion,
} from "@/lib/types";
import { ITEM_TYPES, ITEM_LABEL } from "./itinerary-utils";
import ItineraryItemExtras, { ADDED_BY_NONE } from "./ItineraryItemExtras";

interface ItineraryItemDialogProps {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Item being edited; null = create mode. */
  editing: ItemSegment | null;
  /** Trip day dates for the day picker (YYYY-MM-DD). */
  dayDates: string[];
  /** Default day to preselect when creating. */
  defaultDate?: string;
  /** Default item type to preselect when creating (from the quick-add menu / deep-link). */
  defaultType?: ItineraryItemType;
  /** Default city to prefill when creating (e.g. the leg's destination city). */
  defaultCity?: string;
  companions: ItineraryCompanion[];
  /** Fired after a successful create/update (and after the dialog's own invalidation). */
  onSaved?: () => void;
}

const NONE = ADDED_BY_NONE;

/** A sensible default start time per type — a gentle hint, fully editable. */
const TIME_HINT: Partial<Record<ItineraryItemType, string>> = {
  lodging: "15:00",
  dining: "19:00",
  activity: "10:00",
};

export default function ItineraryItemDialog({
  tripId,
  open,
  onOpenChange,
  editing,
  dayDates,
  defaultDate,
  defaultType,
  defaultCity,
  companions,
  onSaved,
}: ItineraryItemDialogProps) {
  const queryClient = useQueryClient();
  const [itemType, setItemType] = useState<ItineraryItemType>("activity");
  const [title, setTitle] = useState("");
  const [dayDate, setDayDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [city, setCity] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [isAnchor, setIsAnchor] = useState(false);
  const [addedBy, setAddedBy] = useState<string>(NONE);
  const [err, setErr] = useState<string | null>(null);

  // Hydrate fields whenever the dialog opens (edit -> existing values, create -> blanks).
  useEffect(() => {
    if (!open) return;
    setErr(null);
    if (editing) {
      setItemType(editing.item_type);
      setTitle(editing.title);
      setDayDate(editing.day_date);
      setStartTime(editing.start_time ?? "");
      setEndTime(editing.end_time ?? "");
      setCity(editing.city ?? "");
      setLocation(editing.location ?? "");
      setNotes(editing.notes ?? "");
      setIsAnchor(editing.is_anchor);
      setAddedBy(editing.added_by?.traveler_id ?? NONE);
    } else {
      const type = defaultType ?? "activity";
      setItemType(type);
      setTitle("");
      setDayDate(defaultDate ?? dayDates[0] ?? "");
      setStartTime(TIME_HINT[type] ?? "");
      setEndTime("");
      setCity(defaultCity ?? "");
      setLocation("");
      setNotes("");
      setIsAnchor(false);
      setAddedBy(NONE);
    }
  }, [open, editing, defaultDate, defaultType, defaultCity, dayDates]);

  const mutation = useMutation({
    mutationFn: () => {
      const body: ItineraryItemInput = {
        item_type: itemType,
        title: title.trim(),
        day_date: dayDate,
        start_time: startTime || null,
        end_time: endTime || null,
        city: city.trim() || null,
        location: location.trim() || null,
        notes: notes.trim() || null,
        is_anchor: isAnchor,
        added_by_traveler_id: addedBy === NONE ? null : addedBy,
      };
      return editing
        ? itineraryApi.updateItem(tripId, editing.id, body)
        : itineraryApi.createItem(tripId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itinerary", tripId] });
      onSaved?.();
      onOpenChange(false);
    },
    onError: () => setErr("Could not save. Please try again."),
  });

  const submit = () => {
    if (!title.trim()) return setErr("Give it a title.");
    if (!dayDate) return setErr("Pick a day.");
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit item" : "Add to itinerary"}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="it-type">Type</Label>
              <Select
                value={itemType}
                onValueChange={(v) => setItemType(v as ItineraryItemType)}
              >
                <SelectTrigger id="it-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-50">
                  {ITEM_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {ITEM_LABEL[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="it-day">Day</Label>
              {dayDates.length > 0 ? (
                <Select value={dayDate} onValueChange={setDayDate}>
                  <SelectTrigger id="it-day">
                    <SelectValue placeholder="Pick a day" />
                  </SelectTrigger>
                  <SelectContent className="z-50">
                    {dayDates.map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="it-day"
                  type="date"
                  value={dayDate}
                  onChange={(e) => setDayDate(e.target.value)}
                />
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="it-title">Title</Label>
            <Input
              id="it-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Dinner at Joule, Museum visit…"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="it-start">Start time</Label>
              <Input
                id="it-start"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="it-end">End time</Label>
              <Input
                id="it-end"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="it-city">City</Label>
              <Input
                id="it-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Vancouver"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="it-loc">Location</Label>
              <Input
                id="it-loc"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="123 Main St"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="it-notes">Notes</Label>
            <Input
              id="it-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reservation under Wade, smart casual…"
            />
          </div>

          <ItineraryItemExtras
            companions={companions}
            addedBy={addedBy}
            onAddedByChange={setAddedBy}
            isAnchor={isAnchor}
            onAnchorToggle={() => setIsAnchor((v) => !v)}
          />

          {err && <p className="text-sm text-destructive">{err}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending
              ? "Saving…"
              : editing
                ? "Save changes"
                : "Add to itinerary"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
